import React, { useState, useEffect, useMemo, useContext } from 'react';
import PropTypes from 'prop-types';
import striptags from 'striptags';
import { scInjectDynamicText } from 'api';
import { useGlobalContext } from 'globalContext';
import { Helmet } from 'react-helmet';
import { getFrameAttrs } from 'utils/embed';
import { TitleWrapper, ButtonLaunch, ButtonFullscreen, fullscreenContext } from 'msp-components';
import ButtonShare from 'components/ButtonShare';
import MediaTitle from './MediaTitle';
import ImageTitle from './ImageTitle';
import SlideshowTitle from './SlideshowTitle';
import PinMapTitle from './PinMapTitle';
import PlaylistTitle from './PlaylistTitle';
import TimelineTitle from './TimelineTitle';
import CoverTitle from './CoverTitle';
import PageTitle from './PageTitle';

const Title = ({ containerWidth, routeProps, isHomepage, menuItems, project, data, onSelectMenuItem = () => {} }) => {
  const { isInternalEmbed, setCurrentItemTitle, currentSubItem, setCurrentSubItem } = useGlobalContext();

  const titleComponents = {
    video: MediaTitle,
    audio: MediaTitle,
    image: ImageTitle,
    slideshow: SlideshowTitle,
    'pin map': PinMapTitle,
    playlist: PlaylistTitle,
    timeline: TimelineTitle,
    cover: CoverTitle,
    page: PageTitle,
    'topic tree': undefined,
    'page group': undefined,
  };

  const type = data.type.toLowerCase();

  const { isEmbedded } = getFrameAttrs();

  const fsEnabledTypes = ['playlist'];
  const allowFullscreen = fsEnabledTypes.includes(type);
  const { fullscreenEnabled: fullscreenCabable } = useContext(fullscreenContext);

  const renderedMenuItems = useMemo(() => {
    return menuItems
      .filter(item => !item.conditionalDisplay || item.conditionalDisplayItems.includes(data.uid))
      .map(({ structuredContent, ...rest }) => {
        const sc = structuredContent.filter(item => !item.conditionalDisplay || item.conditionalDisplayItems.includes(data.uid));
        return {
          structuredContent: scInjectDynamicText(sc, data, currentSubItem, project),
          ...rest,
        };
      });
  }, [menuItems, data, currentSubItem, project]);

  const defaultPageTitle = isHomepage ? project.name : `${data.name} | ${project.name}`;
  const [pageTitle, setPageTitle] = useState(defaultPageTitle);
  useEffect(() => setCurrentItemTitle(data.name), [setCurrentItemTitle, data.name]);
  const handlePageTitleChange = titleText => {
    setCurrentItemTitle(titleText || data.name);
    setPageTitle(titleText ? `${titleText} | ${project.name}` : defaultPageTitle);
  };
  const handleSubItemChange = setCurrentSubItem;

  const Component = titleComponents[type];
  const fallback = process.env.NODE_ENV === 'development' ? (
    <div style={{ height: '100%', overflow: 'auto' }}>
      <pre>{JSON.stringify(data, null, 2)}</pre>
    </div>
  ) : null;
  if (!Component) return fallback;

  const childProps = { routeProps, menuItems, ...data };
  const { displayOptions } = childProps;
  const { itemName, itemShortDescription } = displayOptions.menuItems;
  const menuProps = {
    title: itemName ? data.name : null,
    description: itemShortDescription ? data.shortDescription : null,
    items: renderedMenuItems,
    onSelectItem: onSelectMenuItem,
    hidePoweredBy: !isInternalEmbed,
  };

  let { hideControlBar, menuDefaultState } = displayOptions;
  if (containerWidth && containerWidth < 768) menuDefaultState = 'closed';

  const afterControls = type === 'video' ? null : (
    <>
      <ButtonShare />
      <ButtonFullscreen />
      {!fullscreenCabable && isEmbedded && <ButtonLaunch onClick={() => window.open(window.location.href)} />}
    </>
  );

  if (typeof containerWidth === 'undefined') return null;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={striptags(data.shortDescription || project.shortDescription)} />
      </Helmet>
      {hideControlBar ? (
        <Component key={routeProps.match.url} {...childProps} />
      ) : (
        <TitleWrapper
          allowFullscreen={allowFullscreen}
          menuDefaultState={menuDefaultState}
          menuProps={menuProps}
          afterControls={afterControls}
        >
          <Component
            key={routeProps.match.url}
            {...childProps}
            onPageTitleChange={handlePageTitleChange}
            onSubItemChange={handleSubItemChange}
          />
        </TitleWrapper>
      )}
    </>
  );
};

Title.propTypes = {
  containerWidth: PropTypes.number,
  routeProps: PropTypes.object,
  isHomepage: PropTypes.bool,
  menuItems: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    label: PropTypes.string,
    content: PropTypes.string,
    isVisible: PropTypes.bool,
  })),
  project: PropTypes.shape({
    uid: PropTypes.string,
    name: PropTypes.string,
    slug: PropTypes.string,
    type: PropTypes.string,
    shortDescription: PropTypes.string,
    longDescription: PropTypes.string,
    socialImage: PropTypes.string,
    analyticsProvider: PropTypes.string,
    analyticsId: PropTypes.string,
    shareUrl: PropTypes.string,
    publisher: PropTypes.shape({
      name: PropTypes.string,
      url: PropTypes.string,
      twitter: PropTypes.string,
      facebook: PropTypes.string,
    }),
  }),
  data: PropTypes.object,
  onSelectMenuItem: PropTypes.func,
};

export default Title;
